import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowUpTrayIcon, TrashIcon } from '@heroicons/react/24/outline'
import { toast } from 'sonner'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import Button from '../ui/Button'
import PageHeader from '../layout/PageHeader'
import MultiSelect from '../ui/MultiSelect'
import ConfirmDialog from '../ui/ConfirmDialog'
import { useProjects } from '../../contexts/ProjectsContext'
import { useWpsSections } from '../../contexts/WpsSectionsContext'
import { useCatalog } from '../../contexts/CatalogContext'
import { isUserAccompanyingReport } from '../../utils/wpsSectionPersistence'
import {
  createReportUploadUrl,
  deleteReport,
  getReportDownloadUrl,
  putFileToPresignedUrl,
  registerReportMetadata
} from '../../utils/reportsClient'

export type ReportItem = {
  id: string
  name: string
  tag: string
  fileKey: string
}

/**
 * Build the displayable Accompanying Reports list from the raw
 * `sections.reports.items` blob.
 *
 * Visibility is gated by `isUserAccompanyingReport`
 * (utils/wpsSectionPersistence.ts), the single source of truth for which
 * entries surface as user-facing supporting documents — used here AND
 * in the Accompanying Reports card count on `WpsForm`. Filters out
 * malformed entries, soft-deleted entries, and auto-generated WPS/PQR
 * PDFs (which have their own dedicated history views).
 */
function normalizeReportItems(raw: unknown): ReportItem[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter(isUserAccompanyingReport)
    .map((item: any) => ({
      id: String(item.id),
      name: String(item?.name || ''),
      tag: String(item?.tag || 'Other'),
      fileKey: String(item.fileKey)
    }))
}

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB'
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

export default function WpsReports() {
  const { id, pipeId } = useParams()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { getProject } = useProjects()
  const { getSection, sections, refresh } = useWpsSections()
  const { getLabelsMerged, addLabel } = useCatalog()
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((pipe) => pipe.id === pipeId)

  const [reports, setReports] = useState<ReportItem[]>([])
  const [tag, setTag] = useState<string[]>([])
  const [pendingReportName, setPendingReportName] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [downloadBusyId, setDownloadBusyId] = useState<string | null>(null)
  const [deleteBusyId, setDeleteBusyId] = useState<string | null>(null)
  const [deleteConfirmReportId, setDeleteConfirmReportId] = useState<string | null>(null)

  useEffect(() => {
    if (!pipeId) return
    const parsed = getSection('reports')
    const items = parsed?.items
    setReports(normalizeReportItems(items))
  }, [pipeId, getSection, sections])

  useEffect(() => {
    setSelectedFile(null)
    setPendingReportName('')
    setTag([])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [pipeId])

  const tagOptions = useMemo(
    () => getLabelsMerged('report_tag', [...reports.map((r) => r.tag), ...tag]),
    [getLabelsMerged, reports, tag]
  )

  const openDownloadUrl = useCallback(
    async (reportId: string, disposition: 'view' | 'download') => {
      if (!id || !pipeId) return
      setDownloadBusyId(reportId)
      try {
        const { downloadUrl } = await getReportDownloadUrl({
          projectId: id,
          pipeSetId: pipeId,
          reportId
        })
        if (disposition === 'view') {
          window.open(downloadUrl, '_blank', 'noopener')
        } else {
          const link = document.createElement('a')
          link.href = downloadUrl
          link.target = '_blank'
          link.rel = 'noopener'
          const item = reports.find((r) => r.id === reportId)
          link.download = item?.name || 'report'
          link.click()
        }
      } catch (err: any) {
        toast.error(err?.message || 'Failed to get download link')
      } finally {
        setDownloadBusyId(null)
      }
    },
    [id, pipeId, reports]
  )

  const performDeleteReport = async (reportId: string) => {
    if (!id || !pipeId) return
    setDeleteBusyId(reportId)
    try {
      await deleteReport({ projectId: id, pipeSetId: pipeId, reportId })
      await refresh()
      toast.success('Report removed from the list.')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to remove report')
    } finally {
      setDeleteBusyId(null)
      setDeleteConfirmReportId(null)
    }
  }

  const handleAddReport = async () => {
    if (!id || !pipeId || !selectedFile) return
    setIsUploading(true)
    try {
      const displayName = pendingReportName.trim() || selectedFile.name
      const reportTag = tag[0] || 'Other'
      const { uploadUrl, fileKey, reportId } = await createReportUploadUrl({
        projectId: id,
        pipeSetId: pipeId,
        fileName: selectedFile.name,
        contentType: selectedFile.type || 'application/octet-stream'
      })
      await putFileToPresignedUrl(uploadUrl, selectedFile)
      await registerReportMetadata({
        projectId: id,
        pipeSetId: pipeId,
        body: {
          reportId,
          name: displayName,
          tag: reportTag,
          fileKey
        }
      })
      await refresh()
      setTag([])
      setPendingReportName('')
      setSelectedFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
      toast.success('Report uploaded successfully.')
    } catch (err: any) {
      toast.error(err?.message || 'Upload failed')
    } finally {
      setIsUploading(false)
    }
  }

  const resetUploadForm = () => {
    setTag([])
    setPendingReportName('')
    setSelectedFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const fileCountLabel = selectedFile ? 1 : 0

  if (!project || !pipeSet) {
    return (
      <Card title="Reports not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to continue.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog
        open={deleteConfirmReportId !== null}
        title="Confirm deletion of report"
        description="Are you sure you want to remove this report from the list?"
        // description="Remove this report from the list? The file stays in storage (soft delete)."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        tone="danger"
        confirmBusy={deleteBusyId !== null}
        onCancel={() => {
          if (deleteBusyId === null) setDeleteConfirmReportId(null)
        }}
        onConfirm={async () => {
          if (deleteConfirmReportId) await performDeleteReport(deleteConfirmReportId)
        }}
      />

      <PageHeader
        backTo={{ onClick: () => navigate(-1), label: 'Back' }}
        title={
          <>
            <span>Accompanying Reports</span>
            <span className="text-heritage-grey/60">•</span>
            <span className="truncate text-text-grey">{project.name}</span>
          </>
        }
        subtitle={
          <>
            <span>{(project.endClients?.[0] ?? project.client)} • {project.location}</span>
            <Badge tone="primary">
              {pipeSet.nominalOd} {pipeSet.nominalOdUnit} × {pipeSet.wallThickness}{' '}
              {pipeSet.wallThicknessUnit}
            </Badge>
          </>
        }
      />

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-xs font-semibold text-gray-700">Documents</p>
        <div className="mt-3 space-y-2 text-xs text-gray-600">
          {reports.length === 0 && <p>No reports uploaded yet.</p>}
          {reports.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm text-gray-700">{item.name}</p>
                <Badge tone="gray">{item.tag}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  className="h-7 px-2 text-[11px]"
                  variant="secondary"
                  disabled={!item.fileKey || downloadBusyId === item.id}
                  onClick={() => void openDownloadUrl(item.id, 'view')}
                >
                  View
                </Button>
                <Button
                  className="h-7 px-2 text-[11px]"
                  variant="secondary"
                  disabled={!item.fileKey || downloadBusyId === item.id}
                  onClick={() => void openDownloadUrl(item.id, 'download')}
                >
                  Download
                </Button>
                <button
                  type="button"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 shadow-sm transition hover:bg-gray-50 hover:text-error-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={deleteBusyId === item.id}
                  title="Remove report"
                  aria-label="Remove report"
                  onClick={() => setDeleteConfirmReportId(item.id)}
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="border-b border-gray-100 pb-4">
          <h3 className="text-sm font-semibold text-gray-900">Upload new report</h3>
          <p className="mt-1 text-xs text-gray-500">
            Add supporting documents for this pipe set. Supported types include PDF, Excel, Word, and images.
          </p>
        </div>

        <div className="mt-5 space-y-5">
          <div>
            <label className="text-sm font-semibold text-gray-900">
              Document <span className="text-error-500">*</span>
            </label>
            <p className="mt-0.5 text-xs text-gray-500">Upload documents (PDF, Excel, DOC, etc.)</p>
            <input
              ref={fileInputRef}
              type="file"
              className="sr-only"
              disabled={isUploading}
              onChange={(event) => {
                const file = event.target.files?.[0] ?? null
                setSelectedFile(file)
                if (file && !pendingReportName.trim()) setPendingReportName(file.name.replace(/\.[^/.]+$/, '') || file.name)
              }}
            />
            <button
              type="button"
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
              className="mt-3 flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 px-6 py-12 text-center transition hover:border-primary-300 hover:bg-primary-50/30 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ArrowUpTrayIcon className="h-8 w-8 text-gray-400" />
              {selectedFile ? (
                <>
                  <span className="mt-2 max-w-full truncate px-2 text-sm font-semibold text-gray-800" title={selectedFile.name}>
                    {selectedFile.name}
                  </span>
                  <span className="mt-1 text-xs text-gray-500">{formatFileSize(selectedFile.size)}</span>
                  <span className="mt-2 text-xs font-medium text-primary-600">Change file</span>
                </>
              ) : (
                <>
                  <span className="mt-2 text-sm font-medium text-gray-700">Select Document</span>
                  <span className="mt-1 text-xs text-gray-500">Click to browse from your device</span>
                </>
              )}
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-semibold text-gray-900">Display name</label>
              <p className="mt-0.5 text-xs text-gray-500">Shown in the report list (defaults from file name).</p>
              <input
                className="input-primary mt-2"
                placeholder="Report display name"
                value={pendingReportName}
                disabled={isUploading}
                onChange={(e) => setPendingReportName(e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-900">Tag</label>
              <p className="mt-0.5 text-xs text-gray-500">Choose one tag to classify this document.</p>
              <MultiSelect
                className="mt-2"
                values={tag}
                options={tagOptions}
                placeholder="Select tag..."
                addLabel="Add New Tag"
                createModalTitle="Add New Tag"
                createFieldLabel="Tag name"
                createInputPlaceholder="Enter tag name"
                searchPlaceholder="Search tags..."
                interactionLocked={isUploading}
                onChange={(values) => setTag(values.slice(-1))}
                onCreateOption={async (value) => {
                  await addLabel('report_tag', value)
                  setTag([value])
                }}
                inputClassName="h-10 text-xs"
              />
            </div>
          </div>

          <div className="flex flex-col gap-4 border-t border-gray-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-gray-500">Current files: {fileCountLabel} file(s)</p>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                disabled={isUploading}
                onClick={resetUploadForm}
              >
                Cancel
              </Button>
              <button
                type="button"
                disabled={!selectedFile || isUploading}
                className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold"
                onClick={() => void handleAddReport()}
              >
                <ArrowUpTrayIcon className="h-4 w-4" />
                {isUploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
